import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import MenuItem from '@mui/material/MenuItem';
import { NavLink, useLocation } from 'react-router-dom';
// import LogoImage from '../../assets/logo.svg';
import ProfileImage from '../../assets/profile.svg';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ListItemIcon, ListItemText } from '@mui/material';
import { getNavbarData } from '../../utils/getNavbarData';
import './Navbar.css';
import { useAuth } from '../../contexts/AuthContext';
import { useLoading } from '../../contexts/LoadingContext';

// Public routes that don't need auth
const PUBLIC_PATHS = ['/jobs', '/unauthorized'];

const Navbar: React.FC = () => {
  const location = useLocation();
  const { keycloak, authenticated } = useAuth();
  const { isLoading } = useLoading();

  const is_hr = authenticated && keycloak.hasResourceRole('hr');
  const { pages, settings } = React.useMemo(() => getNavbarData(is_hr), [is_hr]);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(
    null,
  );
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  // Check if current path is public
  const isPublicPath = PUBLIC_PATHS.some(path =>
    location.pathname === path || location.pathname.startsWith('/jobs/')
  );

  // On protected routes, hide navbar while loading or if not authenticated
  if (!isPublicPath && (isLoading || !authenticated)) {
    return null;
  }

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#373737',
      },
    },
  });
  // const location = useLocation();
  // if (location.pathname === '/login') {
  //     return null;
  // }
  return (
    <ThemeProvider theme={darkTheme}>
      <AppBar position="sticky">
        <Container maxWidth={false}>
          <Toolbar disableGutters sx={{ minHeight: '36px !important' }}>
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {/* Small screen display */}
                {pages.map(page => (
                  <MenuItem key={page.path}>
                    <NavLink
                      to={page.path}
                      style={{
                        color: 'white',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                      }}
                      className={({ isActive }) => (isActive ? 'active' : '')}
                    >
                      <ListItemText
                        primary={page.text}
                        sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                      />
                    </NavLink>
                  </MenuItem>
                ))}
                {/* Settings items for small screens */}
                {settings.filter(s => s.path).map(setting => (
                  <MenuItem key={setting.path}>
                    <NavLink
                      to={setting.path}
                      style={{
                        color: 'white',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        whiteSpace: 'nowrap',
                      }}
                      className={({ isActive }) => (isActive ? 'active' : '')}
                    >
                      <ListItemText
                        primary={setting.text}
                        sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                      />
                    </NavLink>
                  </MenuItem>
                ))}
                {authenticated && (
                  <MenuItem
                    key="logout"
                    onClick={() => {
                      handleCloseNavMenu();
                      keycloak.logout({
                        redirectUri: window.location.origin,
                      });
                    }}
                  >
                    <ListItemText
                      primary="Logout"
                      sx={{
                        marginTop: 'auto',
                        marginBottom: 'auto',
                      }}
                    />
                  </MenuItem>
                )}
              </Menu>
            </Box>
            <Typography
              variant="h6"
              noWrap
              sx={{
                mr: 2,
                display: 'flex',
                justifyContent: 'center',
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: '.3rem',
                color: 'inherit',
                textDecoration: 'none',
              }}
            >
              <NavLink to="/" className="logo-container">

                <img
                  src="/TTG_FEC_Logo.svg"
                  alt="TTG Logo"
                  style={{ height: '40px', display: 'block' }}
                  className="navbar-logo"
                />
              </NavLink>
            </Typography>
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'flex-end',
                height: '100%',
              }}
            >
              {pages.map(page => (
                <NavLink
                  key={page.path}
                  to={page.path}
                  style={{
                    color: 'white',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '20px',
                    paddingRight: '40px',
                  }}
                  className={({ isActive }) =>
                    isActive ? 'active main' : 'main'
                  }
                >
                  {page.Icon && (
                    <ListItemIcon sx={{ minWidth: 'auto', marginRight: '8px' }}>
                      <page.Icon style={{ color: 'white' }} />
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={page.text}
                    sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                  />
                </NavLink>
              ))}
            </Box>

            <Box sx={{ flexGrow: 0 }}>
              <Box
                onClick={handleOpenUserMenu}
                sx={{
                  flexGrow: 1,
                  display: { xs: 'none', md: 'flex' },
                  justifyContent: 'flex-end',
                  cursor: 'pointer',
                }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', marginRight: '8px' }}>
                  <img
                    src={ProfileImage}
                    alt="Logo"
                    style={{ height: '25px' }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="My Account"
                  sx={{ marginTop: 'auto', marginBottom: 'auto' }}
                />
              </Box>

              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                autoFocus={false}
              >
                {settings.map(setting => (
                  <MenuItem
                    key={`${setting.text}${setting.path}`}
                    onClick={() => {
                      handleCloseUserMenu();
                      if (setting.text === 'Logout') {
                        keycloak.logout({
                          redirectUri: window.location.origin,
                        });
                      }
                    }}
                  >
                    {setting.path ? (
                      <NavLink
                        to={setting.path}
                        style={{
                          color: 'white',
                          textDecoration: 'none',
                          whiteSpace: 'nowrap',
                        }}
                        className={({ isActive }) => (isActive ? 'active' : '')}
                      >
                        <ListItemText primary={setting.text} />
                      </NavLink>
                    ) : (
                      <ListItemText primary={setting.text} />
                    )}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </ThemeProvider>
  );
};
export default Navbar;
